import { z } from "zod";
import {
  isoDateTimeSchema,
  readMethodSchema,
  severitySchema,
  sourceSchema,
  uuidSchema,
} from "./common.js";

/** One value an attribute holds or held, with its provenance (docs/DATA_MODEL.md section 2). */
export const attributeValueSchema = z.object({
  value: z.unknown(),
  source: sourceSchema,
  sourceRef: z.string(),
  observedAt: isoDateTimeSchema,
  recordedAt: isoDateTimeSchema,
  confidence: z.number().min(0).max(1),
  evidenceIds: z.array(uuidSchema),
  isCurrent: z.boolean(),
  inConflict: z.boolean(),
});
export type AttributeValue = z.infer<typeof attributeValueSchema>;

export const premisesSchema = z.object({
  id: uuidSchema,
  uprn: z.string().nullable(),
  addressLines: z.array(z.string()),
  postcode: z.string(),
  useClass: z.string().nullable(),
});
export type Premises = z.infer<typeof premisesSchema>;

export const locationTypeSchema = z.enum([
  "internal",
  "external_chamber",
  "external_boundary_box",
  "other",
  "unknown",
]);
export const meterStatusSchema = z.enum([
  "active",
  "removed",
  "faulty_suspected",
  "faulty_confirmed",
]);

export const meterSchema = z.object({
  id: uuidSchema,
  serial: z.string(),
  manufacturer: z.string(),
  sizeMm: z.number().int(),
  dials: z.number().int(),
  readMethod: readMethodSchema,
  locationType: locationTypeSchema,
  locationDescription: z.string().nullable(),
  gridRef: z.string().nullable(),
  accessNotes: z.string().nullable(),
  installedOn: z.string().date(),
  removedOn: z.string().date().nullable(),
  status: meterStatusSchema,
});
export type Meter = z.infer<typeof meterSchema>;

export const meterWithProvenanceSchema = meterSchema.extend({
  attributes: z.record(z.array(attributeValueSchema)),
});
export type MeterWithProvenance = z.infer<typeof meterWithProvenanceSchema>;

export const readTypeSchema = z.enum([
  "actual",
  "customer",
  "estimated",
  "smart_interval",
  "smart_daily",
]);

export const meterReadSchema = z.object({
  id: uuidSchema,
  readAt: isoDateTimeSchema,
  value: z.number(),
  readType: readTypeSchema,
  source: sourceSchema,
  rollover: z.boolean(),
});
export type MeterRead = z.infer<typeof meterReadSchema>;

export const supplyPointSummarySchema = z.object({
  id: uuidSchema,
  spid: z.string(),
  service: z.enum(["water", "sewerage"]),
  address: z.string(),
  retailerName: z.string(),
  readMethod: readMethodSchema,
  lastActualReadAt: isoDateTimeSchema.nullable(),
  confidenceScore: z.number().int().min(0).max(100),
  openFindings: z.number().int().nonnegative(),
  topSeverity: severitySchema.nullable(),
  isSynthetic: z.boolean(),
});
export type SupplyPointSummary = z.infer<typeof supplyPointSummarySchema>;

export const supplyPointDetailSchema = supplyPointSummarySchema.extend({
  attributes: z.record(attributeValueSchema),
  premises: premisesSchema,
  meters: z.array(meterWithProvenanceSchema),
  confidenceBreakdown: z.object({
    base: z.number(),
    penalty: z.number(),
    components: z.record(z.number()),
  }),
});
export type SupplyPointDetail = z.infer<typeof supplyPointDetailSchema>;

export const ledgerEventSchema = z.object({
  id: uuidSchema,
  seq: z.number().int(),
  type: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  payload: z.record(z.unknown()),
  actor: z.string(),
  source: sourceSchema,
  evidenceIds: z.array(uuidSchema),
  occurredAt: isoDateTimeSchema,
  recordedAt: isoDateTimeSchema,
  hash: z.string(),
});
export type LedgerEvent = z.infer<typeof ledgerEventSchema>;

/** A timeline row: a ledger event, or a marker the API derives from the ledger (D-35). */
export const timelineEntrySchema = z.object({
  occurredAt: isoDateTimeSchema,
  type: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  summary: z.string().optional(),
  derived: z.boolean(),
  derivedKind: z.enum(["missing_meter_exchange"]).nullable().optional(),
  event: ledgerEventSchema.nullable().optional(),
});
export type TimelineEntry = z.infer<typeof timelineEntrySchema>;
